import React, { useRef, useState } from "react";
import { Upload, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, API } from "@/lib/api";

/**
 * Reusable image upload widget.
 * - Shows current preview (if `value`)
 * - Click / drop to select
 * - POSTs to /api/admin/upload and calls onChange(fullUrl)
 */
export const ImageUploader = ({ value, onChange, aspect = "aspect-[4/5]", label = "Imagine", testId, allowRemove = false }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const toAbsolute = (url) => {
    if (!url) return "";
    if (/^https?:\/\//i.test(url)) return url;
    if (url.startsWith("/api/")) return `${API.replace(/\/api$/, "")}${url}`;
    return url;
  };

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Selectează o imagine, te rog.");
      return;
    }
    if (file.size > 6 * 1024 * 1024) {
      toast.error("Imaginea depășește 6 MB.");
      return;
    }
    const form = new FormData();
    form.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/admin/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Imagine încărcată");
    } catch (err) {
      toast.error(formatApiError(err, "Nu am putut încărca imaginea"));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      {label && <p className="text-xs uppercase tracking-[0.2em] text-[#F7F1E8]/78">{label}</p>}
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          handleFile(e.dataTransfer.files?.[0]);
        }}
        className={`relative mt-2 flex ${aspect} w-full items-center justify-center overflow-hidden rounded-2xl border-2 border-dashed border-[color-mix(in_srgb,var(--milk)_18%,transparent)] bg-[#2B1B13] transition-colors hover:border-[color-mix(in_srgb,var(--accent)_60%,transparent)]`}
      >
        {value ? (
          <>
            <img src={toAbsolute(value)} alt="preview" className="h-full w-full object-cover" />
            <div className="absolute bottom-3 right-3 flex gap-2">
              {allowRemove && (
                <button
                  type="button"
                  onClick={() => onChange("")}
                  data-testid={testId ? `${testId}-remove` : undefined}
                  className="inline-flex items-center gap-1.5 rounded-full bg-[#2B1B13]/95 px-3 py-2 text-xs uppercase tracking-[0.15em] text-red-500 shadow-sm ring-1 ring-red-100 hover:bg-[#2B1B13]"
                >
                  <Trash2 className="h-3.5 w-3.5" strokeWidth={1.6} />
                  Șterge
                </button>
              )}
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                data-testid={testId ? `${testId}-replace` : undefined}
                className="inline-flex items-center gap-2 rounded-full bg-[#2B1B13]/95 px-4 py-2 text-xs uppercase tracking-[0.2em] text-[#F7F1E8] shadow-sm ring-1 ring-[color-mix(in_srgb,var(--milk)_18%,transparent)] hover:bg-[#2B1B13]"
              >
                <Upload className="h-3.5 w-3.5" strokeWidth={1.6} />
                Schimbă
              </button>
            </div>
          </>
        ) : (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            data-testid={testId ? `${testId}-pick` : undefined}
            className="flex flex-col items-center gap-2 p-8 text-center text-[#F7F1E8]/68 hover:text-[#F7F1E8]"
          >
            {uploading ? (
              <Loader2 className="h-6 w-6 animate-spin" strokeWidth={1.4} />
            ) : (
              <Upload className="h-6 w-6" strokeWidth={1.4} />
            )}
            <span className="text-xs uppercase tracking-[0.2em]">
              {uploading ? "Se încarcă…" : "Trage imaginea aici sau apasă"}
            </span>
            <span className="text-[11px] text-[#F7F1E8]/58">JPG · PNG · WEBP · maxim 6 MB</span>
          </button>
        )}
        {uploading && value && (
          <div className="absolute inset-0 flex items-center justify-center bg-[#2B1B13]/70">
            <Loader2 className="h-6 w-6 animate-spin text-[#F7F1E8]/78" strokeWidth={1.4} />
          </div>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        data-testid={testId}
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
    </div>
  );
};

/** Turn a relative /api/files/... URL into an absolute URL usable in <img src>. */
export const absoluteImageUrl = (url) => {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("/api/")) return `${API.replace(/\/api$/, "")}${url}`;
  return url;
};

export default ImageUploader;
